/**
 * Per-splat effect parameters (the values the timeline animates and the
 * config rows tune) and their identity defaults.
 *
 * In 2.x these were material uniforms on the splat's own shader. In 3.x every
 * splat is projected by one compute shader, so the values are kept here per
 * element and handed to the projector as uniforms for that splat's placement
 * (see the renderer hook). With every value at its identity the effects are
 * a no-op, so an unanimated splat renders exactly as upstream draws it.
 */

import type { Element } from '../element';

/** Identity values, in the order the projector's effects block expects them. */
export const SPLAT_EFFECT_DEFAULTS: Readonly<Record<string, number[]>> = {
    // reveal / fade / dissolve / grow
    uRevealCenter: [0, 0, 0],
    uRevealRadius: [1e9],
    uRevealFade: [0],
    uFadeAlpha: [1],
    uDissolveAmount: [0],
    uGrowScale: [1],
    // particle fly-in
    uParticleProgress: [1],
    uParticleSpread: [5],
    uParticleDirection: [0, 1, 0],
    uParticleDuration: [0.3],
    uParticleFlash: [1],
    // radial wave
    uRadialEnabled: [0],
    uRadialEngineMode: [0],
    uRadialTime: [0],
    uRadialCenter: [0, 0, 0],
    uRadialSpeed: [1],
    uRadialAcceleration: [5],
    uRadialDelay: [2],
    uRadialDotTint: [0, 1, 1],
    uRadialLiftTint: [1, 1, 1],
    uRadialOscillation: [0.1],
    uRadialEndRadius: [25],
    uRadialBandWidth: [1],
    uRadialDotSize: [0.05],
    uRadialDotTintMix: [1],
    uRadialLiftTintMix: [1],
    // rain
    uRainEnabled: [0],
    uRainTime: [0],
    uRainCenter: [0, 0, 0],
    uRainDistance: [30],
    uRainSpeed: [3],
    uRainAcceleration: [0],
    uRainFlightTime: [2],
    uRainSize: [0.015],
    uRainRotation: [0.9],
    uRainFallTint: [0, 1, 1],
    uRainFallTintIntensity: [0.2],
    uRainHitTint: [2, 0, 0],
    uRainHitDuration: [0.5],
    uRainEndRadius: [25],
    // grid eruption
    uGridEnabled: [0],
    uGridTime: [0],
    uGridCenter: [0, 0, 0],
    uGridBlockCount: [10],
    uGridBlockSize: [2],
    uGridDelay: [0.2],
    uGridDuration: [1],
    uGridDotSize: [0.01],
    uGridMoveTint: [1, 0, 1],
    uGridMoveTintIntensity: [0.2],
    uGridLandTint: [2, 2, 0],
    uGridLandDuration: [0.6],
    uGridEndRadius: [25]
};

const store = new WeakMap<Element, Map<string, number[]>>();
const versions = new WeakMap<Element, number>();
const bump = (element: Element) => versions.set(element, (versions.get(element) ?? 0) + 1);

/** Bumps on every write; the renderer's packer repacks when it moves. */
export const splatEffectVersion = (element: Element): number => versions.get(element) ?? 0;

/** Current value (identity default when never set). */
export const getSplatEffect = (element: Element, name: string): number[] => {
    const v = store.get(element)?.get(name);
    if (v) return v.slice();
    const d = SPLAT_EFFECT_DEFAULTS[name];
    return d ? d.slice() : [0];
};

export const setSplatEffect = (element: Element, name: string, value: number[]): void => {
    let map = store.get(element);
    if (!map) {
        map = new Map();
        store.set(element, map);
    }
    map.set(name, value.slice());
    bump(element);
};

/** True when any value differs from its identity — the renderer skips the effects pass otherwise. */
export const splatHasEffects = (element: Element): boolean => {
    const map = store.get(element);
    if (!map) return false;
    for (const [name, value] of map) {
        const d = SPLAT_EFFECT_DEFAULTS[name];
        if (!d) continue;
        for (let i = 0; i < d.length; i++) {
            if (value[i] !== d[i]) return true;
        }
    }
    return false;
};

/** Every parameter for an element, defaults filled in, for the renderer. */
export const splatEffectParams = (element: Element): Record<string, number[]> => {
    const out: Record<string, number[]> = {};
    const map = store.get(element);
    for (const [name, d] of Object.entries(SPLAT_EFFECT_DEFAULTS)) {
        out[name] = (map?.get(name) ?? d).slice();
    }
    return out;
};

export const clearSplatEffects = (element: Element): void => {
    store.delete(element);
    bump(element);
};
